/**
 * File: useProtectedRouteController.ts
 * Path: apps/admin/src/modules/auth/hooks/useProtectedRouteController.ts
 * Description: The access decision for ProtectedRoute: waits, redirects, or lets the page through. The
 * decision itself lives in routeAccessUtils.ts so it can be tested without a router.
 */

import { useLocation } from "react-router-dom";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { useRole } from "@/modules/auth/hooks/useRoleQueries";
import { useAuthQueries } from "@/modules/auth/hooks/useAuthQueries";
import { resolveRouteAccess } from "@/modules/auth/utils/routeAccessUtils";
import type { Permission } from "@idk/types";

export const useProtectedRouteController = (requires?: Permission) => {
  const { user, isLoading } = useAuth();
  const { isViewer, can } = useRole();
  const location = useLocation();

  // Shares the react-query cache with useAuthController, so this is the same
  // profile fetch the rest of the app already makes.
  const profileQuery = useAuthQueries().useProfile(user?.uid);

  const decision = resolveRouteAccess({
    isLoading: isLoading || (!!user && profileQuery.isLoading),
    isAuthenticated: !!user,
    isDisabled: profileQuery.data?.disabled === true,
    isViewer,
    can,
    requires,
  });

  return { decision, location };
};
